import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import "./react-bootstrap-table.css";
import PropTypes from "prop-types";
import { setActiveProject } from "../../actions/ui";
import {
    selectFilteredProjectsInActiveFeature,
    selectAllFundData,
    selectActiveProject,
} from "../../reducers/";
import { projectShape } from "../../modules/project";
import styles from "./listview.module.scss";
import { fields } from "../../modules/fields";

const DownloadIcon = props => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" {...props}>
        <path
            fill="currentColor"
            d="M480 352H346.5l-45.25 45.25C289.2 409.3 273.1 416 256 416s-33.16-6.656-45.25-18.75L165.5 352H32c-17.67 0-32 14.33-32 32v96c0 17.67 14.33 32 32 32h448c17.67 0 32-14.33 32-32v-96c0-17.7-14.3-32-32-32zm-48 104c-13.2 0-24-10.8-24-24s10.8-24 24-24 24 10.8 24 24-10.8 24-24 24zm-198.6-81.4c6.2 6.3 14.4 9.4 22.6 9.4s16.38-3.125 22.62-9.375l128-128c12.49-12.5 12.49-32.75 0-45.25-12.5-12.5-32.76-12.5-45.25 0L288 274.8V32c0-17.67-14.33-32-32-32-17.7 0-32 14.33-32 32v242.8l-73.4-73.4c-12.49-12.5-32.75-12.5-45.25 0-12.49 12.5-12.49 32.75 0 45.25L233.4 374.6z"
        />
    </svg>
);

const getDataFormat = (key, setActiveProject, fundData) => {
    switch (key) {
        case "Project":
            return function (cell, row) {
                return (
                    <a
                        style={{ cursor: "pointer" }}
                        alt={"View details for: " + cell}
                        href={""}
                        id={row.applicationNo}
                        onClick={e => {
                            e.preventDefault();
                            setActiveProject(row);
                        }}
                    >
                        {cell}
                    </a>
                );
            };
        case "grantApprovedAmount":
            return function (cell) {
                if (typeof cell === "number") {
                    return (
                        <span
                            tabIndex={0}
                            aria-label={`${cell.toLocaleString()} dollars`}
                        >
                            ${cell.toLocaleString()}
                        </span>
                    );
                } else {
                    return <span tabIndex={0}>{cell}</span>;
                }
            };
        case "fundName":
            return function (cell) {
                if (fundData[cell]) {
                    return <span tabIndex={0}>{fundData[cell].displayName}</span>;
                } else {
                    return <span tabIndex={0}>{cell}</span>;
                }
            };

        default:
            return function (cell) {
                return <span tabIndex={0}>{cell}</span>;
            };
    }
};

const toString = x => {
    if (typeof x === "string") {
        return x;
    } else {
        return x.toString();
    }
};

const Listview = ({ projects, setActiveProject, activeProject, fundData }) => {
    let ids = [];
    const dedupedProjects = projects.filter(d => {
        if (d.ID === undefined) {
            return d;
        }
        if (!ids.includes(d.ID)) {
            ids.push(d.ID);
            return d;
        }
        return null;
    });
    const PROJECT_LENGTH = dedupedProjects.length;

    // Keep track of the cell that was focused
    // So we can return focus to it after modal closes
    const ref = useRef(null);

    // Open link on click
    useEffect(() => {
        document.addEventListener("keypress", e => {
            if (e.key === "Enter") {
                if (e.target && e.target.getElementsByTagName("a").length) {
                    const id = e.target.getElementsByTagName("a")[0].id;
                    const matchingProject = projects.find(
                        p => toString(p.applicationNo) === toString(id)
                    );
                    if (matchingProject) {
                        ref.current = e.target;
                        setActiveProject(matchingProject);
                    }
                }
            }
        });
    }, [projects, setActiveProject]);

    // Return focus to cell after modal closes
    useEffect(() => {
        if (!activeProject && ref && ref.current) {
            ref.current.focus();
        }
    }, [activeProject]);

    const createCustomExportCSVButton = onClick => {
        return (
            <button
                className={styles.exportCSVButton}
                onClick={onClick}
                tabIndex={1}
                title={"Export to SVG."}
            >
                <DownloadIcon height={16} width={16} />
                <span>Export to CSV</span>
            </button>
        );
    };

    const options = {
        exportCSVBtn: createCustomExportCSVButton,
    };

    return (
        <div className={styles.listView}>
            <hr />
            <div className={styles.container}>
                <p
                    tabIndex={1}
                    aria-label={`${PROJECT_LENGTH} projects in current search criteria`}
                    className={styles.numResults}
                >
                    {PROJECT_LENGTH} projects
                </p>
                <div className={styles.tableWrapper}>
                    <BootstrapTable
                        data={dedupedProjects}
                        striped={false}
                        hover={false}
                        pagination={false}
                        exportCSV={true}
                        scrollTop={"Top"}
                        options={options}
                    >
                        {fields
                            .filter(field => field.listView)
                            .map(field => {
                                return (
                                    <TableHeaderColumn
                                        key={field.key}
                                        dataField={field.key}
                                        dataSort={true}
                                        isKey={field.isKey || false}
                                        width={field.listViewWidth}
                                        dataFormat={getDataFormat(
                                            field.key,
                                            setActiveProject,
                                            fundData
                                        )}
                                    >
                                        {field.listViewLabel || field.label}
                                    </TableHeaderColumn>
                                );
                            })}
                    </BootstrapTable>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = state => ({
    activeProject: selectActiveProject(state),
    projects: selectFilteredProjectsInActiveFeature(state),
    fundData: selectAllFundData(state),
});
const mapDispatchToProps = { setActiveProject };

Listview.propTypes = {
    setActiveProject: PropTypes.func.isRequired,
    projects: PropTypes.arrayOf(PropTypes.shape(projectShape)),
    fundData: PropTypes.object.isRequired,
    activeProject: PropTypes.object,
};
export default connect(mapStateToProps, mapDispatchToProps)(Listview);



// WEBPACK FOOTER //
// ./src/components/listview/listview.js